import { parseErrorAuth } from '@/libs/authError'
import i18n from '@/libs/i18n'

enum Errors {
    InvalidData = 1,
    UserNotConnectedToManager,
    InvalidPassword,
    NotFound,
    InternalServerError,
    InvalidEmailOrPassword,
    InvalidFileType,
    AuthAPIError,
    InvalidCaptcha,
    ForgottenPasswordRequestExpired,
    InvalidEmail,
    Unauthorized
}

interface ApiError {
    code: number
    message?: string
}

/**
 * Extracts the error payload from a response body.
 *
 * The spec-aligned APIs wrap it as `{ "error": { code, message } }`; before the
 * alignment admin-api emitted a bare `{ code, message }`. Both shapes are
 * accepted so the frontend and the API can be released independently.
 */
function extractApiError(errorObject: unknown): ApiError | null {
    if (errorObject === null || typeof errorObject !== 'object') return null

    const wrapped = (errorObject as { error?: unknown }).error
    const candidate = (wrapped !== null && typeof wrapped === 'object') ? wrapped : errorObject

    if (!('code' in (candidate))) return null

    const { code, message } = candidate as { code: unknown, message?: unknown }
    if (typeof code !== 'number') return null

    return { code, message: typeof message === 'string' ? message : undefined }
}

export function parseError(errorObject: unknown, fallback = '') {
    const t = i18n.global.t
    const apiError = extractApiError(errorObject)

    if (apiError === null || (apiError.code as Errors) === Errors.InternalServerError) {
        if (fallback && fallback !== '') return fallback
        return t('errors.generic_error')
    }

    switch (apiError.code as Errors) {
        case Errors.InvalidData:
            return t('api_errors.invalid_data')
        case Errors.UserNotConnectedToManager:
            return t('api_errors.user_not_connected_to_manager')
        case Errors.InvalidPassword:
            return t('api_errors.invalid_password')
        case Errors.NotFound:
            return t('api_errors.not_found')
        case Errors.InternalServerError:
            return t('api_errors.internal_server_error')
        case Errors.InvalidEmailOrPassword:
            return t('api_errors.invalid_email_or_password')
        case Errors.InvalidFileType:
            return t('api_errors.invalid_file_type')
        case Errors.AuthAPIError:
            return parseErrorAuth(apiError.message || '')
        case Errors.InvalidCaptcha:
            return t('api_errors.invalid_captcha')
        case Errors.ForgottenPasswordRequestExpired:
            return t('api_errors.forgotten_password_expired')
        case Errors.InvalidEmail:
            return t('api_errors.invalid_email')
        case Errors.Unauthorized:
            return t('api_errors.unauthorized')
        default:
            return t('errors.generic_error')
    }
}
